import { useState } from "react";
import { useClerk, useUser, useSession } from "@clerk/clerk-react";
import { C, FONT_STACK } from "../constants/colors";
import { TERMS_VERSION, TERMS_URL, PRIVACY_URL } from "../constants/legal";
import BrillLogo from "./BrillLogo";

// Shown to anyone who is signed in and has Brill access but has not yet agreed to
// the current TERMS_VERSION. Nothing else in Brill renders until they do — this is
// where consent is collected, because accounts are created by the GHL webhook and
// nobody ever sees a sign-up form.

export function ConsentScreen() {
  const { signOut } = useClerk();
  const { user } = useUser();
  const { session } = useSession();

  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const email = user?.primaryEmailAddress?.emailAddress;

  const accept = async () => {
    setSaving(true);
    setError(null);
    try {
      const token = await session?.getToken();
      const res = await fetch("/api/accept-terms", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ version: TERMS_VERSION }),
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.error || "Could not save your agreement");
      }
      // Pull the updated publicMetadata down so the gate in App.jsx lets us through.
      await user?.reload();
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const linkStyle = { color: C.teal, fontWeight: 600, textDecoration: "underline" };

  return (
    <div style={{
      minHeight: "100vh", background: C.indigo,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      padding: "20px", fontFamily: FONT_STACK,
    }}>
      <div style={{ textAlign: "center", width: "100%", maxWidth: 420 }}>
        <BrillLogo height={72} style={{ margin: "0 auto 24px" }} />

        <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 20px 60px rgba(0,0,0,0.3)", padding: "28px 24px", textAlign: "left" }}>
          <h2 style={{ margin: "0 0 10px", fontSize: 18, fontWeight: 700, color: C.indigo }}>Before you start</h2>
          <p style={{ margin: "0 0 20px", fontSize: 14, color: "#666", lineHeight: 1.55 }}>
            Please review and agree to our terms{email ? <> for <strong style={{ color: C.indigo }}>{email}</strong></> : ""}. You only need to do this once.
          </p>

          <label style={{
            display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer",
            background: `${C.teal}0d`, border: `1.5px solid ${agreed ? C.teal : "#e0dfe8"}`,
            borderRadius: 10, padding: "14px 14px", marginBottom: 16,
          }}>
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              style={{ width: 18, height: 18, marginTop: 1, accentColor: C.teal, flexShrink: 0, cursor: "pointer" }}
            />
            <span style={{ fontSize: 14, color: C.indigo, lineHeight: 1.5 }}>
              I have read and agree to the{" "}
              <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" style={linkStyle} onClick={(e) => e.stopPropagation()}>Terms &amp; Conditions</a>
              {" "}and the{" "}
              <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" style={linkStyle} onClick={(e) => e.stopPropagation()}>Privacy Policy</a>.
            </span>
          </label>

          {error && (
            <p style={{ margin: "0 0 14px", fontSize: 13, color: "#b3261e", lineHeight: 1.5 }}>{error}</p>
          )}

          <button
            onClick={accept}
            disabled={!agreed || saving}
            style={{
              width: "100%", background: agreed ? C.teal : "#d9d7e0", color: "#fff",
              border: "none", borderRadius: 10, padding: "12px 16px",
              fontSize: 15, fontWeight: 600, fontFamily: FONT_STACK,
              cursor: agreed && !saving ? "pointer" : "not-allowed",
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? "Saving…" : "Agree and continue"}
          </button>
        </div>

        <button
          onClick={() => signOut()}
          style={{
            marginTop: 18, background: "none", border: "none",
            color: "#888", fontSize: 13, cursor: "pointer", textDecoration: "underline",
          }}
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
